import React from 'react';
import { useQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { Appointment, GeneralRequest } from '../../interfaces';
import {
  getBookingRequests,
  getGeneralRequests,
} from '../../services/appointments';
import { SortableKeys } from '../../types/appointments';
import { SortValues } from '../../types/ui';

function useGetRequests({
  status,
  enabled = true,
  sort,
  employeeIds,
}: {
  status: 'ACCEPTED' | 'REJECTED' | 'REQUESTED' | 'ARCHIVED';
  enabled?: boolean;
  sort?: SortValues<SortableKeys>;
  employeeIds?: string[];
}) {
  const { user } = useUser();

  const sortedEmployeeIds = employeeIds?.sort();

  const queryKey = React.useMemo(
    () => [
      'requests',
      {
        status,
        employeeId: user?.employee?.employeeId,
        businessId: user?.business?.id,
        role: user?.role,
        sortOrder: sort?.order,
        sortOrderBy: sort?.orderBy,
        employeeIds: sortedEmployeeIds,
      },
    ],
    [
      status,
      user?.employee?.employeeId,
      user?.business?.id,
      user?.role,
      sort?.order,
      sort?.orderBy,
      sortedEmployeeIds,
    ],
  );

  const { data, isLoading, isRefetching, isFetching, isError, refetch } =
    useQuery<{
      appointments: Appointment[];
      generalRequests: GeneralRequest[];
    }>({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey,
      queryFn: async () => {
        if (!user) throw new Error('No user found');
        const appointments: Appointment[] = await getBookingRequests({
          businessId: user.business.id,
          status,
          sort,
          employeeIds: sortedEmployeeIds,
        });
        const generalRequests =
          user.role === 'SHOP_MANAGER' &&
          user.generalRequestOwner &&
          (status === 'REQUESTED' || status === 'ARCHIVED')
            ? await getGeneralRequests({
                businessId: user.business.id,
                sort,
                deletedOnly: status === 'ARCHIVED',
                excludeDeleted: status !== 'ARCHIVED',
                employeeIds: sortedEmployeeIds,
              })
            : [];
        return {
          appointments,
          generalRequests,
        };
      },
      enabled: !!user && enabled,
      // after 1 minute, the data is considered stale and will be refetched when it's queried (on mount, or when refetch is called)
      staleTime: 60000,
    });

  // sort manually if general requests are included - 2 separate tables
  const combinedAppointments = React.useMemo(() => {
    if (!data?.generalRequests) return data?.appointments ?? [];

    const appointments = [...data.appointments, ...data.generalRequests];

    if (!sort?.order || !sort?.orderBy)
      return appointments.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ); // If no sort is provided, sort by the default createdAt descending

    return appointments.sort((a, b) => {
      const first = sort.order === 'asc' ? a : b;
      const second = sort.order === 'asc' ? b : a;
      if (sort.orderBy === 'createdAt') {
        return new Date(first.createdAt) < new Date(second.createdAt) ? -1 : 1;
      }
      if (sort.orderBy === 'customer') {
        return first.customer.firstName.localeCompare(
          second.customer.firstName,
        );
      }
      if (sort.orderBy === 'employee') {
        const firstEmployeeName =
          'employee' in first
            ? first.employee?.displayName || first.employee?.firstName
            : 'Any Artist';
        const secondEmployeeName =
          'employee' in second
            ? second.employee?.displayName || second.employee?.firstName
            : 'Any Artist';

        return firstEmployeeName.localeCompare(secondEmployeeName);
      }
      if (sort.orderBy === 'tattooLocation') {
        return (first.tattooLocation || '').localeCompare(
          second.tattooLocation || '',
        );
      }
      return 0;
    });
  }, [data?.appointments, data?.generalRequests, sort?.order, sort?.orderBy]);

  return {
    appointments: data?.appointments,
    generalRequests: data?.generalRequests,
    combinedAppointments,
    isLoading,
    isRefetching,
    isFetching,
    isError,
    refetch,
  };
}

export default useGetRequests;
